<div class="admin-embeddings">
  <div class="page-header">
    <div class="header-content">
      <div class="header-left">
        <button class="btn-back" (click)="goBack()">
          <span class="back-icon">←</span>
          <span>Back to Dashboard</span>
        </button>
      </div>
      <div class="header-center">
        <h1 class="page-title">RAG Embeddings Management</h1>
        <p class="page-subtitle">Manage AI search embeddings for listings</p>
      </div>
      <div class="header-right">
        <button
          class="btn-refresh"
          (click)="loadUnembeddedCount()"
          [disabled]="loadingCount"
        >
          <span class="refresh-icon">🔄</span>
          <span>Refresh</span>
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="error" class="alert alert-error">
    <div class="alert-icon">⚠️</div>
    <div class="alert-content">
      <h3>Error</h3>
      <p>{{ error }}</p>
    </div>
    <button class="alert-close" (click)="clearMessages()">✕</button>
  </div>

  <div *ngIf="successMessage" class="alert alert-success">
    <div class="alert-icon">✅</div>
    <div class="alert-content">
      <h3>Success</h3>
      <p>{{ successMessage }}</p>
    </div>
    <button class="alert-close" (click)="clearMessages()">✕</button>
  </div>

  <div class="main-content">
    <div class="section-card">
      <div class="section-header">
        <h2 class="section-title">
          <span class="section-icon">📊</span>
          Unembedded Listings Status
        </h2>
      </div>

      <div *ngIf="loadingCount" class="loading-container">
        <div class="loading-spinner-large"></div>
        <p>Checking unembedded listings...</p>
      </div>

      <div *ngIf="!loadingCount && unembeddedData" class="stats-grid">
        <div class="stat-item">
          <div class="stat-icon">📋</div>
          <div class="stat-content">
            <div class="stat-number">{{ unembeddedData.totalUnembedded }}</div>
            <div class="stat-label">Unembedded Listings</div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-icon">💰</div>
          <div class="stat-content">
            <div class="stat-number">
              ${{ formatCurrency(unembeddedData.estimatedCost) }}
            </div>
            <div class="stat-label">Estimated Cost</div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-icon">📦</div>
          <div class="stat-content">
            <div class="stat-number">
              {{ unembeddedData.recommendedBatches }}
            </div>
            <div class="stat-label">Recommended Batches</div>
          </div>
        </div>

        <div class="stat-item" [class]="getSafetyStatusClass()">
          <div class="stat-icon">🛡️</div>
          <div class="stat-content">
            <div class="stat-number">{{ unembeddedData.safetyLimit }}</div>
            <div class="stat-label">Safety Limit</div>
            <div class="stat-status">{{ getSafetyStatusText() }}</div>
          </div>
        </div>
      </div>

      <div *ngIf="!loadingCount && unembeddedData" class="info-section">
        <div
          class="info-card"
          [ngClass]="{ warning: !unembeddedData.withinSafetyLimit }"
        >
          <div class="info-icon">
            <span *ngIf="unembeddedData.withinSafetyLimit">ℹ️</span>
            <span *ngIf="!unembeddedData.withinSafetyLimit">⚠️</span>
          </div>
          <div class="info-content">
            <h4 *ngIf="unembeddedData.withinSafetyLimit">Ready to Process</h4>
            <h4 *ngIf="!unembeddedData.withinSafetyLimit">
              Safety Limit Exceeded
            </h4>
            <p *ngIf="unembeddedData.withinSafetyLimit">
              All {{ unembeddedData.totalUnembedded }} unembedded listings are
              within the safety limit of {{ unembeddedData.safetyLimit }}. You
              can safely process all listings at once.
            </p>
            <p *ngIf="!unembeddedData.withinSafetyLimit">
              You have {{ unembeddedData.totalUnembedded }} unembedded listings,
              which exceeds the safety limit of
              {{ unembeddedData.safetyLimit }}. Please process in smaller
              batches or contact development team.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="section-card">
      <div class="section-header">
        <h2 class="section-title">
          <span class="section-icon">⚡</span>
          Process Embeddings
        </h2>
      </div>

      <div class="process-section">
        <div class="process-info">
          <h3>What happens when you process embeddings?</h3>
          <ul>
            <li>AI embeddings are generated for all unembedded listings</li>
            <li>This enables semantic search capabilities for users</li>
            <li>The process uses OpenAI's text-embedding-3-small model</li>
            <li>Cost is approximately $0.00002 per listing</li>
            <li>Processing cannot be undone once started</li>
          </ul>
        </div>

        <div class="process-actions">
          <button
            class="btn-process"
            (click)="processAllListings()"
            [disabled]="isProcessButtonDisabled()"
            [ngClass]="{
              processing: loadingProcess,
              disabled: isProcessButtonDisabled(),
              ready:
                unembeddedData?.withinSafetyLimit &&
                (unembeddedData?.totalUnembedded ?? 0) > 0
            }"
          >
            <span *ngIf="loadingProcess" class="loading-spinner"></span>
            <span *ngIf="!loadingProcess" class="process-icon">🚀</span>
            <span>{{ getProcessButtonText() }}</span>
          </button>

          <div
            *ngIf="unembeddedData && (unembeddedData.totalUnembedded ?? 0) > 0"
            class="process-details"
          >
            <p><strong>Cost:</strong> {{ unembeddedData.estimatedCost }}</p>
            <p>
              <strong>Listings:</strong> {{ unembeddedData.totalUnembedded }}
            </p>
            <p><strong>Status:</strong> {{ getSafetyStatusText() }}</p>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="processResult" class="section-card">
      <div class="section-header">
        <h2 class="section-title">
          <span class="section-icon">📈</span>
          Last Processing Result
        </h2>
      </div>

      <div class="result-grid">
        <div class="result-item success">
          <div class="result-icon">✅</div>
          <div class="result-content">
            <div class="result-number">{{ processResult.totalProcessed }}</div>
            <div class="result-label">Processed</div>
          </div>
        </div>

        <div class="result-item">
          <div class="result-icon">🔍</div>
          <div class="result-content">
            <div class="result-number">{{ processResult.totalFound }}</div>
            <div class="result-label">Found</div>
          </div>
        </div>

        <div class="result-item">
          <div class="result-icon">📊</div>
          <div class="result-content">
            <div class="result-number">{{ processResult.tablesProcessed }}</div>
            <div class="result-label">Tables Processed</div>
          </div>
        </div>

        <div class="result-item">
          <div class="result-icon">💰</div>
          <div class="result-content">
            <div class="result-number">{{ processResult.estimatedCost }}</div>
            <div class="result-label">Cost</div>
          </div>
        </div>
      </div>

      <div class="result-message">
        <p>{{ processResult.message }}</p>
        <small
          >Processed at: {{ processResult.timestamp | date : "medium" }}</small
        >
      </div>
    </div>
  </div>
</div>
